/** Digit with position metadata for rounding-to-decimal-places visualisation. */
export interface DpDigit {
  readonly char: string;
  readonly role: 'integer' | 'point' | 'keep' | 'check';
}

export interface AnswerDigit {
  readonly char: string;
  readonly isPoint: boolean;
  readonly changed: boolean;
}

export interface DpBreakdown {
  readonly digits: DpDigit[];
  readonly answerDigits: AnswerDigit[];
  readonly rounded: string;
  readonly checkDigitValue: number;
  readonly roundedUp: boolean;
}

/**
 * Break a number into per-character roles for rounding to `dp` decimal places.
 *
 * Roles:
 *  - `integer`  digits before the decimal point (always kept, not highlighted)
 *  - `point`    the decimal point character
 *  - `keep`     the first `dp` digits after the point (pink)
 *  - `check`    the very next digit after the kept ones (indigo)
 */
export function breakdownDp(value: number, dp: number): DpBreakdown {
  const precision = Math.max(dp + 3, 8);
  const raw = Math.abs(value).toFixed(precision);
  const isNegative = value < 0;

  const dotIdx = raw.indexOf('.');
  const digits: DpDigit[] = [];

  if (isNegative) {
    digits.push({ char: '-', role: 'integer' });
  }

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (i < dotIdx) {
      digits.push({ char: ch, role: 'integer' });
    } else if (i === dotIdx) {
      digits.push({ char: '.', role: 'point' });
    } else {
      const decPos = i - dotIdx; // 1-based position after decimal
      if (decPos <= dp) {
        digits.push({ char: ch, role: 'keep' });
      } else if (decPos === dp + 1) {
        digits.push({ char: ch, role: 'check' });
        break;
      }
    }
  }

  const checkDigitValue = Number(raw[dotIdx + dp + 1]) || 0;
  const roundedUp = checkDigitValue >= 5;
  const rounded = formatWithTrailingZeros(value, dp);

  // Build answer digits aligned to the same grid columns
  const answerChars = rounded.split('');
  const answerDigits: AnswerDigit[] = [];
  for (let i = 0; i < digits.length; i++) {
    if (i < answerChars.length) {
      const ch = answerChars[i];
      answerDigits.push({ char: ch, isPoint: ch === '.', changed: roundedUp && ch !== digits[i].char });
    } else {
      answerDigits.push({ char: '', isPoint: false, changed: false });
    }
  }

  return { digits, answerDigits, rounded, checkDigitValue, roundedUp };
}

function formatWithTrailingZeros(value: number, dp: number): string {
  return Number(value).toFixed(dp);
}

/** Generate a random number suitable for d.p. rounding practice. */
export function randomDpNumber(): number {
  const templates = [
    () => +(Math.random() * 20 - 5).toFixed(4),
    () => +(Math.random() * 100).toFixed(4),
    () => +(Math.random() * 0.1).toFixed(5),
    () => +(9 + Math.random() * 4).toFixed(4),
    () => +(Math.random() * 1000).toFixed(3),
  ];
  const fn = templates[Math.floor(Math.random() * templates.length)];
  return fn();
}
